import styled from '@emotion/styled';

import { Pagination, Table, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { FlatCard } from '~/subscription-management/components';

import { usePrettifyPrice } from '~/subscription-management/utils/cost/prettify-price';
import { prettifyDate } from '~/subscription-management/utils/date.utils';
import type { InvoiceItem } from './invoice-list.types';
import { InvoiceListItem } from './InvoiceListItem';
import { ITEMS_PER_PAGE, usePaginatedInvoiceList } from './usePaginatedInvoiceList';

interface Props {
  invoices: InvoiceItem[];
  currency: Currency;
}

const S = {
  Container: styled(FlatCard)`
    padding: 2px 0; // show border radius without bothering with Table styles too much
  `,
  HeadCell: styled(Table.HeadCell)`
    background-color: ${tokens.color.ui_background};
  `,
  PaginationContainer: styled.div`
    background: ${tokens.color.ui_background};
    padding: ${tokens.spacer.x3};
    display: flex;
    justify-content: flex-end;
  `,
};

export function InvoiceList({ invoices, currency }: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:invoices.invoice-list');
  const { prettifyPrice } = usePrettifyPrice(currency);
  const { currentPage, list, setCurrentPage, setPageSize, pageSize } =
    usePaginatedInvoiceList(invoices);

  return (
    <S.Container>
      <Table caption="Invoices" tableName="InvoicesTable">
        <Table.Head>
          <Table.HeadRow>
            <S.HeadCell>{t('status')}</S.HeadCell>
            <S.HeadCell>{t('date-issued')}</S.HeadCell>
            <S.HeadCell>{t('amount')}</S.HeadCell>
            <S.HeadCell>{}</S.HeadCell>
          </Table.HeadRow>
        </Table.Head>
        <Table.Body>
          {list.map(({ amountWithTax, createdAt, invoiceNumber, status, documentUrl }) => (
            <InvoiceListItem
              key={invoiceNumber}
              total={prettifyPrice(amountWithTax)}
              id={invoiceNumber}
              issueDate={prettifyDate(createdAt)}
              status={status}
              link={documentUrl !== '' ? documentUrl : undefined}
            />
          ))}
        </Table.Body>
      </Table>
      {invoices.length > pageSize && (
        <S.PaginationContainer>
          <Pagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalCount={invoices.length}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            withSpacer
            itemsPerPageLabel={t('items-per-page')}
            pageSizes={ITEMS_PER_PAGE}
          />
        </S.PaginationContainer>
      )}
    </S.Container>
  );
}
